import { InfoOutlined } from "@mui/icons-material"
import {
  alpha,
  Box,
  Chip,
  colors,
  LinearProgress,
  Stack,
  TableCell,
  TableRow,
  Tooltip,
  useMediaQuery,
  Theme,
  Typography,
} from "@mui/material"
import { useQuery } from "@tanstack/react-query"
import React from "react"

import { getAllLiquidityChanges, type LiquidityChange } from "../../api/lp-api"
import { IdBlock } from "../../components/IdBlock"
import { InMemoryTable } from "../../components/InMemoryTable"
import { LoadingSkeletons } from "../../components/LoadingSkeletons"
import { formatFullDate, formatRelative } from "../../utils/date-utils"
import { EntityBlock } from "@/components/EntityBlock"
import { SimpleNumberFormat } from "@/components/SimpleNumberFormat"
import { TokenAmountBlock } from "@/components/TokenAmountBlock"
import { usePool } from "@/context/PoolContext"
import { useTokenInfo } from "@/hooks/use-token-info"

export function LiquidityTable() {
  const { pool } = usePool()

  const [quoteToken] = useTokenInfo(pool.quoteToken)
  const [baseToken] = useTokenInfo(pool.baseToken)

  const {
    data: lpChanges,
    isLoading,
    isRefetching,
  } = useQuery({
    queryKey: ["getAllLiquidityChanges", pool.id],
    queryFn: async () => await getAllLiquidityChanges(pool),
  })

  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"))

  if (!pool || isLoading || !lpChanges) {
    return <LoadingSkeletons />
  }

  if (isMobile) {
    return (
      <>
        {isRefetching && (
          <LinearProgress color="primary" variant="indeterminate" sx={{ height: 2 }} />
        )}
        {lpChanges.map((item) => {
          const { type, quantity, id, from, baseTokenQuantity, quoteTokenQuantity } = item
          const color = type === "provide" ? colors.green[500] : colors.red[500]

          return (
            <TableRow hover key={item.id}>
              <TableCell colSpan={9}>
                <Stack gap={1}>
                  <Stack direction="row" gap={1} alignItems="center">
                    <Chip
                      label={type === "provide" ? "Add" : "Remove"}
                      sx={{
                        borderRadius: 1,
                        color,
                        background: alpha(color, 0.1),
                        fontWeight: "inherit",
                      }}
                    />
                    <Tooltip title={formatFullDate(new Date(item.ingestedAt))}>
                      <Typography variant="body2">
                        {formatRelative(new Date(item.ingestedAt))}
                      </Typography>
                    </Tooltip>
                    <Typography variant="body2" color="text.secondary">
                      Ref: {item.ref}
                    </Typography>
                  </Stack>

                  <Stack direction="row" gap={2} alignItems="center">
                    <Typography variant="body2">
                      Value:{" "}
                      <TokenAmountBlock
                        amount={quantity}
                        tokenId={pool.id}
                        needsParsing
                        // currency={false}
                        showTicker={false}
                        hideCopyToClipboard
                      />
                    </Typography>
                    <Typography variant="body2">
                      {baseToken?.ticker}:{" "}
                      <TokenAmountBlock
                        amount={baseTokenQuantity}
                        tokenId={pool.baseToken}
                        needsParsing
                        hideCopyToClipboard
                      />
                    </Typography>
                    <Typography variant="body2">
                      {quoteToken?.ticker}:{" "}
                      <TokenAmountBlock
                        amount={quoteTokenQuantity}
                        tokenId={pool.quoteToken}
                        needsParsing
                        hideCopyToClipboard
                      />
                    </Typography>
                  </Stack>

                  <Stack direction="row" gap={2} alignItems="center">
                    <Stack direction="row" gap={0.5} alignItems="center">
                      <Typography variant="body2">From:</Typography>
                      <EntityBlock
                        entityId={from}
                        poolId={pool.id}
                        quoteToken={pool.quoteToken as string}
                        baseToken={pool.baseToken as string}
                      />
                    </Stack>
                    <IdBlock label="Tx" value={id} href={`https://ao.link/#/message/${id}`} />
                  </Stack>
                </Stack>
              </TableCell>
            </TableRow>
          )
        })}
      </>
    )
  }

  return (
    <Box sx={{ background: "var(--mui-palette-paperAlt)" }}>
      {isRefetching && (
        <LinearProgress color="primary" variant="indeterminate" sx={{ height: 2 }} />
      )}
      <InMemoryTable
        sx={{
          "& .MuiTableBody-root": {
            maxHeight: 500,
            overflowY: "auto",
          },
        }}
        headerCells={[
          {
            field: "ingestedAt" satisfies keyof LiquidityChange,
            label: (
              <Stack direction="row" gap={0.5} alignItems="center">
                Seen at
                <Tooltip title="The UTC timestamp when the transaction was first detected by the gateway.">
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Stack>
            ),
            sx: { width: 160 },
            // sortable: true,
          },
          {
            field: "ref" satisfies keyof LiquidityChange,
            label: (
              <Stack direction="row" gap={0.5} alignItems="center">
                Sequence Id
                <Tooltip title="The unique ordering ID for the transaction/message within the liquidity pool process.">
                  <InfoOutlined fontSize="small" />
                </Tooltip>
              </Stack>
            ),
            sx: { width: 140 },
            align: "right",
          },
          { label: "Type", sx: { width: 140 } },
          {
            label: "Value",
            align: "right",
          },
          {
            label: baseToken?.ticker,
            align: "right",
            sx: { paddingRight: 5 },
          },
          {
            label: quoteToken?.ticker,
            align: "right",
            sx: { paddingRight: 5 },
          },
          { label: "From", sx: { width: 220 } },
          { label: "Tx", sx: { width: 140 } },
        ]}
        initialSortDir="desc"
        initialSortField={"ref" satisfies keyof LiquidityChange}
        data={lpChanges}
        renderRow={(item: LiquidityChange) => {
          const { type, quantity, id, from, baseTokenQuantity, quoteTokenQuantity } = item

          const color = type === "provide" ? colors.green[500] : colors.red[500]

          return (
            <TableRow
              hover
              key={item.id}
              sx={{
                "& .MuiTableCell-root": {
                  color,
                },
              }}
            >
              <TableCell variant="text">
                <Tooltip title={formatFullDate(new Date(item.ingestedAt))}>
                  <span>{formatRelative(new Date(item.ingestedAt))}</span>
                </Tooltip>
              </TableCell>
              <TableCell align="right" variant="number">
                {item.ref}
              </TableCell>
              <TableCell variant="text">
                <Chip
                  label={type === "provide" ? "Add" : "Remove"}
                  sx={{
                    borderRadius: 1,
                    color,
                    background: alpha(color, 0.1),
                    fontWeight: "inherit",
                    fontSize: "inherit",
                  }}
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <SimpleNumberFormat
                  amount={quantity}
                  tokenId={pool.id}
                  needsParsing
                  currency={false}
                  showTicker={false}
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <TokenAmountBlock
                  amount={baseTokenQuantity}
                  tokenId={pool.baseToken}
                  needsParsing
                  hideCopyToClipboard
                />
              </TableCell>
              <TableCell align="right" variant="number">
                <TokenAmountBlock
                  amount={quoteTokenQuantity}
                  tokenId={pool.quoteToken}
                  needsParsing
                  hideCopyToClipboard
                />
              </TableCell>
              <TableCell variant="text">
                <EntityBlock
                  entityId={from}
                  poolId={pool.id}
                  quoteToken={pool.quoteToken as string}
                  baseToken={pool.baseToken as string}
                />
              </TableCell>
              <TableCell variant="text">
                <IdBlock label="View Tx" value={id} href={`https://ao.link/#/message/${id}`} />
              </TableCell>
            </TableRow>
          )
        }}
      />
    </Box>
  )
}
